import React, { useEffect, useState } from 'react';
import { gql, useQuery } from "@apollo/client";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from "recharts";

const GET_PEDIDOS = gql`
  query getPedidos {
    getPedidos {
      _id
      fecha
      productos
    }
  }
`;

function HistoricoUltimos30dias() {
  const { loading, error, data } = useQuery(GET_PEDIDOS);

  const [filtroInput, setFiltroInput] = useState("");
  const [filtroBuscar, setFiltroBuscar] = useState("");

  const [sugerencias, setSugerencias] = useState([]);
  const [listaProductos, setListaProductos] = useState([]);

  const [dias, setDias] = useState([]);
   const [dias180, setDias180] = useState([]);
  const [semanas, setSemanas] = useState([]); // 🔥 NUEVO

  // ============================================================
  // 🔥 EXTRAER LISTA ÚNICA DE PRODUCTOS PARA AUTOCOMPLETE
  // ============================================================
  useEffect(() => {
    if (!data?.getPedidos) return;

    const nombres = new Set();

    data.getPedidos.forEach(pedido => {
      const productos = JSON.parse(pedido.productos || "[]");
      productos.forEach(p => {
        if (p.nombre) nombres.add(p.nombre.toLowerCase());
      });
    });

    setListaProductos(Array.from(nombres));
  }, [data]);

  // ============================================================
  // 🔥 PROCESAR ESTADÍSTICAS (DÍAS + SEMANAS)
  // ============================================================
  const procesarEstadisticas = () => {
    if (!data?.getPedidos) return;

    const productoBuscado = filtroBuscar.trim().toLowerCase();
    if (productoBuscado === "") return;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const hace30dias = new Date(hoy);
    hace30dias.setDate(hoy.getDate() - 29);

    const hace180dias = new Date(hoy);
hace180dias.setDate(hoy.getDate() - 179);


    // ------------------------------------------------------------
    // 🔵 ESTADÍSTICA DÍA POR DÍA (30 días)
    // ------------------------------------------------------------
    const mapaDias = {};

    for (let i = 0; i < 30; i++) {
      const f = new Date(hoy);
      f.setDate(hoy.getDate() - i);
      const label = f.toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "2-digit"
      });
      mapaDias[label] = 0;
    }

     const mapa180dias = {};

    for (let i = 0; i < 180; i++) {
      const f = new Date(hoy);
      f.setDate(hoy.getDate() - i);
      const label = f.toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "2-digit"
      });
      mapa180dias[label] = 0;
    }

    // ------------------------------------------------------------
    // 🔵 ESTADÍSTICA POR SEMANAS (4 semanas = 28 días)
    // ------------------------------------------------------------
    const mapaSemanas = {
      "Semana 1": 0, // 0 - 6 días
      "Semana 2": 0, // 7 - 13
      "Semana 3": 0, // 14 - 20
      "Semana 4": 0  // 21 - 27
    };

   data.getPedidos.forEach(pedido => {
  const fecha = new Date(pedido.fecha);
  fecha.setHours(fecha.getHours() - 5);

  const diffDias = Math.floor((hoy - fecha) / (1000 * 60 * 60 * 24));

  const labelDia = fecha.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit"
  });

  const productos = JSON.parse(pedido.productos || "[]");

  productos.forEach(p => {
    const nombre = p.nombre.toLowerCase();
    if (!nombre.includes(productoBuscado)) return;

    // ✅ últimos 30 días
    if (fecha >= hace30dias) {
      mapaDias[labelDia] += p.cantidad;

      if (diffDias <= 6) mapaSemanas["Semana 1"] += p.cantidad;
      else if (diffDias <= 13) mapaSemanas["Semana 2"] += p.cantidad;
      else if (diffDias <= 20) mapaSemanas["Semana 3"] += p.cantidad;
      else if (diffDias <= 27) mapaSemanas["Semana 4"] += p.cantidad;
    }

    // ✅ últimos 180 días
    if (fecha >= hace180dias) {
      mapa180dias[labelDia] += p.cantidad;
    }
  });
});

    // Convertir a listas para Recharts
    const arregloDias = Object.entries(mapaDias).reverse().map(([dia, cantidad]) => ({
      dia,
      cantidad
    }));

      const arreglo180Dias = Object.entries(mapa180dias).reverse().map(([dia, cantidad]) => ({
      dia,
      cantidad
    }));

    const arregloSemanas = Object.entries(mapaSemanas).map(([semana, cantidad]) => ({
      semana,
      cantidad
    }));

    setDias(arregloDias);
    setDias180(arreglo180Dias)
    setSemanas(arregloSemanas);

  };

  useEffect(() => {
    procesarEstadisticas();
  }, [data, filtroBuscar]);

  // ============================================================
  // 🔥 AUTOCOMPLETE
  // ============================================================
  const manejarCambio = (texto) => {
    setFiltroInput(texto);

    if (texto.trim() === "") {
      setSugerencias([]);
      return;
    }

    const suger = listaProductos.filter(p =>
      p.includes(texto.toLowerCase())
    );

    setSugerencias(suger.slice(0, 8));
  };

  const seleccionarSugerencia = (nombre) => {
    setFiltroInput(nombre);
    setFiltroBuscar(nombre);
    setSugerencias([]);
  };

  // ============================================================

  if (loading) return <p>Cargando...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div>
      <h2>Ventas últimos 30 días</h2>

      {/* INPUT */}
      <input
        type="text"
        placeholder="Buscar producto..."
        value={filtroInput}
        onChange={(e) => manejarCambio(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") seleccionarSugerencia(filtroInput);
        }}
        style={{ padding: 8, marginBottom: 5 }}
      />

      {/* LISTA DE SUGERENCIAS */}
      {sugerencias.length > 0 && (
        <div
          style={{
            border: "1px solid #ccc",
            padding: 5,
            background: "white",
            borderRadius: 5,
            width: "250px",
            marginBottom: "10px",
            position: "absolute",
            zIndex: 10
          }}
        >
          {sugerencias.map((s, index) => (
            <div
              key={index}
              onClick={() => seleccionarSugerencia(s)}
              style={{
                padding: 6,
                cursor: "pointer",
                borderBottom: "1px solid #eee"
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}

      {/* ============================ */}
      {/* 🔵 GRÁFICA DÍA POR DÍA */}
      {/* ============================ */}
      {filtroBuscar.trim() !== "" && (
        <>
          <h3>Ventas por día (últimos 30 días)</h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dias}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#1a862e80">
                <LabelList dataKey="cantidad" position="top" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* ============================ */}
          {/* 🔵 GRÁFICA POR SEMANAS */}
          {/* ============================ */}
          <h3>Estadística por semanas (últimas 4 semanas)</h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={semanas}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="semana" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#0084ff80">
                <LabelList dataKey="cantidad" position="top" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

           <h3>Ventas por día (últimos 180 días)</h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dias180}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dia" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#5d706080">
                <LabelList dataKey="" position="top" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>

        </>
      )}
    </div>
  );
}

export default HistoricoUltimos30dias;
